import { Box, Tooltip, TooltipProps } from "@mui/material";
import Button from "@mui/material/Button";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DoDisturbOnIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { useTranslation } from "react-i18next";
import { useCompanyBadgeActions } from "../hooks/useCompanyBadgeActions";
import { IPersonBadge } from "../../../../../../common/types";
import { useTableToolbarSelectStyles } from "./TableToolbarSelect.styles";
import { FilterProps, SelectedBadgeRows } from "../../../../types";
import { ToolbarFilters } from "../filters/ToolbarFilters";
import { useTableToolbarSelect } from "./hooks/useTableToolbarSelect/useTableToolbarSelect";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LoadingButton from "../../../../../common/buttons/loading/LoadingButton";
import { useSelector } from "react-redux";
import { metadataSelector } from "../../../../../../redux/metadata/selectors/metadata.selectors";

type Props = {
  selectedRows: SelectedBadgeRows;
  openPrintDialog: (badges: IPersonBadge[]) => void;
  openDeactivateDialog: (badges: IPersonBadge[]) => void;
  onMakePaymentClick: () => void;
} & Omit<FilterProps, "config" | "filterTableData">;

export const TableToolbarSelect = ({
  selectedRows,
  openPrintDialog,
  openDeactivateDialog,
  filterGetters,
  filterSetters,
  onMakePaymentClick
}: Props) => {
  const classes = useTableToolbarSelectStyles();
  const { t } = useTranslation("common");
  const { resendEmails, loading, isOffline } = useCompanyBadgeActions();
  const { isPrintingDisabled, isEmailResendDisabled, isDeactivationDisabled, isMakePaymentDisabled } = useTableToolbarSelect({
    selectedRows
  });
  const { fontsLoaded } = useSelector(metadataSelector);

  const handlePrintClick = () => {
    openPrintDialog(selectedRows.badges);
  };

  const handleDeactivateClick = () => {
    openDeactivateDialog(selectedRows.badges);
  };

  const handleResendEmailsClick = () => {
    resendEmails(selectedRows.badges);
  };

  const isButtonDisabled = loading || isOffline;
  const isMakeButtonDisabled = isButtonDisabled || isMakePaymentDisabled;
  const isDeactivateButtonDisabled = isButtonDisabled || isDeactivationDisabled;
  const isResendEmailButtonDisabled = isButtonDisabled || isEmailResendDisabled;

  const getTooltipProps = (isDisabled: boolean) => {
    return {
      arrow: true,
      title: isDisabled ? t("companyPage.table.actionUnavailable") : "",
      placement: "bottom" as TooltipProps["placement"]
    };
  };

  return (
    <>
      <Box className={classes.container}>
        <Box className={classes.leftContainer}>
          {selectedRows.badges.length} {t("companyPage.table.badgesSelected")}
        </Box>
        <Box className={classes.actionButtons}>
          <Tooltip {...getTooltipProps(isMakeButtonDisabled)}>
            <div>
              <Button data-testid="make-payment-button" disabled={isMakeButtonDisabled} className={classes.button} onClick={onMakePaymentClick}>
                <CreditCardIcon />
                <span>{t("search.badgeDetails.makePayment")}</span>
              </Button>
            </div>
          </Tooltip>

          <Tooltip {...getTooltipProps(isDeactivateButtonDisabled)}>
            <div>
              <Button data-testid="deactivate-button" disabled={isDeactivateButtonDisabled} className={classes.button} onClick={handleDeactivateClick}>
                <DoDisturbOnIcon />
                <span>{t("companyPage.table.deactivateBadges")}</span>
              </Button>
            </div>
          </Tooltip>

          <Tooltip {...getTooltipProps(isResendEmailButtonDisabled)}>
            <div>
              <Button
                data-testid="resend-emails-button"
                disabled={isResendEmailButtonDisabled}
                onClick={handleResendEmailsClick}
                className={classes.button}
                autoCapitalize={"false"}
              >
                <MailOutlineIcon />
                <span>{t("companyPage.table.resendEmails")}</span>
              </Button>
            </div>
          </Tooltip>

          <Tooltip {...getTooltipProps(isPrintingDisabled)}>
            <div>
              <LoadingButton
                data-testid="print-badges-button"
                loading={!fontsLoaded}
                onClick={handlePrintClick}
                disabled={isPrintingDisabled || !fontsLoaded}
                className={`${classes.button} ${classes.printButton} ${!fontsLoaded ? classes.loadingPrintButton : ""}`}
                startIcon={<LocalPrintshopOutlinedIcon />}
              >
                <span>{t("companyPage.table.printBadges")}</span>
              </LoadingButton>
            </div>
          </Tooltip>
        </Box>
      </Box>
      <ToolbarFilters filterSetters={filterSetters} filterGetters={filterGetters} />
    </>
  );
};
